import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WizardApiError } from '../campaign-wizard.api';
import { wz } from './wizard-tokens';
import { WizGradientButton } from './wizard-ui';

interface Props {
  /** Legt eine Kopie an und öffnet sie im Assistenten (wirft bei Fehlern). */
  onDuplicate: () => Promise<void>;
  /** Zurück-Link; ohne Angabe (kompakter Modus in der Laufansicht) entfällt er. */
  onBack?: () => void;
  /** Kompakt: ohne Vollseiten-Zentrierung und ohne Zurück-Link, zum Einbetten in die Laufansicht. */
  compact?: boolean;
}

/** Wurde die Kampagne schon gestartet: Warnung, Duplizieren als neue Kampagne und Zurück-Link (DEV-1096). */
const RunningCampaignNotice: React.FC<Props> = ({ onDuplicate, onBack, compact = false }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const duplicate = async () => {
    setBusy(true);
    setError(null);
    try {
      await onDuplicate();
    } catch (e) {
      setError(`${t('wizDuplicateError')} ${(e as WizardApiError).message ?? ''}`.trim());
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={compact ? 'wiz-running-notice-compact' : 'wiz-running-notice'}
      style={
        compact
          ? { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 12 }
          : {
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 16,
              padding: 24,
            }
      }
    >
      <div
        style={{
          maxWidth: compact ? undefined : 520,
          background: wz.warnBg,
          color: wz.warnFg,
          borderRadius: 12,
          padding: '16px 20px',
          display: 'flex',
          gap: 12,
          alignItems: 'flex-start',
        }}
        role="alert"
      >
        <WarningAmberIcon sx={{ fontSize: 22, marginTop: '1px' }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 14 }}>
          <div style={{ fontWeight: 700 }}>{t('wizRunningTitle')}</div>
          <div>{t('wizRunningText')}</div>
        </div>
      </div>
      {error ? <div style={{ fontSize: 13, color: wz.redFg }}>{error}</div> : null}
      <WizGradientButton onClick={() => void duplicate()} loading={busy}>
        {t('wizDuplicate')}
      </WizGradientButton>
      {onBack ? (
      <button
        type="button"
        onClick={onBack}
        disabled={busy}
        style={{
          color: wz.primary,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textDecoration: 'underline',
          fontSize: 14,
          fontFamily: 'inherit',
        }}
      >
        {t('wizBackToCampaigns')}
      </button>
      ) : null}
    </div>
  );
};

export default RunningCampaignNotice;
